import { useEffect } from "react"
import { useRouterState } from "@tanstack/react-router"

const APP_NAME = "Ambr AI"

// How the browser tab title works:
// - Every route declares a default title via `staticData.title` in
//   lib/router.ts; <RouteTitleSync /> (mounted once in Root) applies the
//   deepest matched route's title on every navigation.
// - Pages with dynamic titles (e.g. a scenario's name) call usePageMeta to
//   override the route default while they're mounted.

// Format and set the tab title, falling back to the bare app name.
export function setDocumentTitle(title: string | undefined) {
  document.title = title ? `${title} - ${APP_NAME}` : APP_NAME
}

// Count of mounted usePageMeta overrides — while > 0, RouteTitleSync leaves
// document.title alone so a page-level dynamic title isn't clobbered.
let overrideCount = 0

// Dynamic per-page override of the route's static title. Passing undefined
// (e.g. while the entity is still loading) leaves the route default in place.
export function usePageMeta(title: string | undefined) {
  useEffect(() => {
    if (title === undefined) return
    overrideCount++
    setDocumentTitle(title)
    return () => {
      overrideCount--
    }
  }, [title])
}

// Mounted once in Root: keeps document.title in sync with the deepest matched
// route's staticData.title. Rendered before the Outlet so its effect runs
// before page effects — a page-level usePageMeta therefore wins on mount.
export function RouteTitleSync() {
  const matches = useRouterState({ select: (state) => state.matches })
  useEffect(() => {
    if (overrideCount > 0) return
    const title = [...matches]
      .reverse()
      .find((match) => match.staticData?.title)?.staticData.title
    setDocumentTitle(title)
  }, [matches])
  return null
}
